import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateLogisticsCalculationInput,
  LogisticsCalculationDetailResponse,
  PatchLogisticsCalculationInput,
} from '@magimp/shared';
import {
  createLogisticsCalculation,
  deleteLogisticsCalculation,
  getLogisticsCalculation,
  getLogisticsCalculationViews,
  getLogisticsCargoCandidate,
  listLogisticsCalculations,
  patchLogisticsCalculation,
  publishLogisticsCalculation,
  revokeLogisticsCalculation,
  searchLogisticsCargoCandidates,
} from '../../api/logistics-calculations.js';

export const logisticsCalculationsKeys = {
  all: ['logistics-calculations'] as const,
  list: () => ['logistics-calculations', 'list'] as const,
  detail: (id: string) => ['logistics-calculations', 'detail', id] as const,
  views: (id: string) => ['logistics-calculations', 'views', id] as const,
  candidates: (query: string) => ['logistics-calculations', 'cargo-candidates', query] as const,
  candidate: (id: string) => ['logistics-calculations', 'cargo-candidate', id] as const,
};

export function useLogisticsCalculationsList() {
  return useQuery({
    queryKey: logisticsCalculationsKeys.list(),
    queryFn: ({ signal }) => listLogisticsCalculations(signal),
  });
}

export function useLogisticsCalculationDetail(id: string) {
  return useQuery({
    queryKey: logisticsCalculationsKeys.detail(id),
    queryFn: ({ signal }) => getLogisticsCalculation(id, signal),
  });
}

export function useLogisticsCalculationViews(id: string) {
  return useQuery({
    queryKey: logisticsCalculationsKeys.views(id),
    queryFn: ({ signal }) => getLogisticsCalculationViews(id, signal),
  });
}

export function useLogisticsCargoCandidates(query: string, active: boolean) {
  const normalized = query.trim();
  return useQuery({
    queryKey: logisticsCalculationsKeys.candidates(normalized),
    queryFn: ({ signal }) => searchLogisticsCargoCandidates(normalized, signal),
    enabled: active && (normalized.length === 0 || normalized.length >= 2),
  });
}

/** Resolves a cargo handed to the editor by another screen, by identity. */
export function useLogisticsCargoCandidate(id: string | undefined) {
  return useQuery({
    queryKey: logisticsCalculationsKeys.candidate(id ?? ''),
    queryFn: ({ signal }) => getLogisticsCargoCandidate(id!, signal),
    enabled: id !== undefined,
    retry: false,
  });
}

function useInvalidate() {
  const client = useQueryClient();
  return (id?: string) => {
    void client.invalidateQueries({ queryKey: logisticsCalculationsKeys.all });
    if (id) void client.invalidateQueries({ queryKey: logisticsCalculationsKeys.detail(id) });
  };
}

export function useCreateLogisticsCalculation() {
  const invalidate = useInvalidate();
  return useMutation<LogisticsCalculationDetailResponse, Error, CreateLogisticsCalculationInput>({
    mutationFn: createLogisticsCalculation,
    onSuccess: (response) => invalidate(response.calculation.id),
  });
}

export function usePatchLogisticsCalculation(id: string) {
  const invalidate = useInvalidate();
  return useMutation<LogisticsCalculationDetailResponse, Error, PatchLogisticsCalculationInput>({
    mutationFn: (input) => patchLogisticsCalculation(id, input),
    onSuccess: () => invalidate(id),
  });
}

export function usePublishLogisticsCalculation(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => publishLogisticsCalculation(id),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteLogisticsCalculation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteLogisticsCalculation(id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: logisticsCalculationsKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: logisticsCalculationsKeys.list() });
    },
  });
}

export function useRevokeLogisticsCalculation(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => revokeLogisticsCalculation(id),
    onSuccess: () => invalidate(id),
  });
}
